import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { Skeleton } from '@mui/material';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import UserHoverCard from 'components/UserHoverCard/UserHoverCard';
import { useHoverCard } from 'hooks/useHoverCard';
import { Points } from 'components/Points/Points';
import { SetIcon } from 'components/SetIcon';
import { formatConditions } from 'utils/wantListHelpers';
import { formatLanguages } from 'constants/languages';
import { getFinishSummary } from 'screens/Main/tabs/Send/components/SendListView/listViewUtils';
import { SidebarTradeCard } from 'screens/Public/UserProfile/context/ProfileTradeSidebarContext';
import ComposeTradeModal from 'screens/Main/tabs/TradeAlerts/components/ComposeTradeModal/ComposeTradeModal';
import { useGetCardWantersQuery, CardWanter } from 'store/services/cards/wanters/cardWantersApi';
import { AssetUrls } from 'config/assets';
import './CardWantersTable.css';

// Floating card-image preview dimensions (card aspect ratio ≈ 488×680).
const PREVIEW_W = 214;
const PREVIEW_H = Math.round((PREVIEW_W * 680) / 488);

// Position the preview beside the hovered set cell, flipping/clamping to stay on-screen.
const computePreviewPos = (el: HTMLElement): { left: number; top: number } => {
  const r = el.getBoundingClientRect();
  let left = r.right + 12;
  if (left + PREVIEW_W > window.innerWidth - 8) {
    left = r.left - PREVIEW_W - 12; // flip to the left of the cell
  }
  if (left < 8) left = 8;
  let top = r.top + r.height / 2 - PREVIEW_H / 2;
  if (top < 8) top = 8;
  if (top + PREVIEW_H > window.innerHeight - 8) top = window.innerHeight - PREVIEW_H - 8;
  return { left, top };
};

interface CardWantersTableProps {
  oracleId: string;
  cardName: string;
  isLoggedIn: boolean;
  /** Called when a logged-out viewer clicks Send — opens the login flow. */
  onRequireLogin: () => void;
}

type SortKey = 'name' | 'quantity' | 'set' | 'foil' | 'condition' | 'language' | 'points';
type SortDir = 'asc' | 'desc';

interface ComposeState {
  recipient: { id: number; username: string };
  stagedCard: SidebarTradeCard;
}

const SET_ICON_BASE = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/sets/icons';

const setIconUrlFor = (setCode?: string): string | null =>
  setCode ? `${SET_ICON_BASE}/${setCode.toLowerCase()}.svg` : null;

// Build the SidebarTradeCard the ComposeTradeModal pre-stages — the card being viewed,
// at the wanter's specific requested printing. Mirrors alertToStagedCard in TradeAlertsPage.
const wanterToStagedCard = (wanter: CardWanter, cardName: string): SidebarTradeCard => ({
  id: `wanter-${wanter.userId}-${wanter.wantListItemId}`,
  wantListId: wanter.wantListItemId,
  cardId: wanter.cardId,
  cardName,
  set: wanter.setName ?? wanter.setCode,
  setCode: wanter.setCode,
  condition: wanter.condition,
  language: wanter.language,
  foil: wanter.foil,
  imageUrl: wanter.imageUrl ?? '',
  pointValue: wanter.pointValue,
  scryfallId: wanter.scryfallId,
  acceptFoil: wanter.acceptFoil ?? true,
  acceptNonFoil: wanter.acceptNonFoil ?? true,
  availableInFoil: wanter.availableInFoil ?? wanter.foil,
  availableInNonFoil: wanter.availableInNonFoil ?? !wanter.foil,
  acceptableSetDetails: wanter.acceptableSetDetails,
  acceptableConditions: wanter.acceptableConditions,
  acceptableLanguages: wanter.acceptableLanguages,
  availableLanguages: wanter.availableLanguages,
  maxQuantity: wanter.quantity,
});

const SortArrow: React.FC<{ active: boolean; dir: SortDir }> = ({ active, dir }) => (
  <span className={`cwt-sort-arrow ${active ? 'active' : ''}`} aria-hidden="true">
    {active ? (dir === 'asc' ? '▲' : '▼') : '▾'}
  </span>
);

interface ColumnDef {
  key: SortKey;
  label: string;
  className?: string;
}

const COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Name', className: 'cwt-col-name' },
  { key: 'quantity', label: 'Qty', className: 'cwt-col-qty' },
  { key: 'set', label: 'Set', className: 'cwt-col-set' },
  { key: 'foil', label: 'Foil', className: 'cwt-col-foil' },
  { key: 'condition', label: 'Condition', className: 'cwt-col-condition' },
  { key: 'language', label: 'Language', className: 'cwt-col-language' },
  { key: 'points', label: 'Points', className: 'cwt-col-points' },
];

interface WanterRowProps {
  wanter: CardWanter;
  cardName: string;
  onSend: (wanter: CardWanter) => void;
}

const WanterRow: React.FC<WanterRowProps> = ({ wanter, cardName, onSend }) => {
  const { triggerProps, cardProps, isVisible, position, data, isLoading } = useHoverCard(
    wanter.username,
  );

  // Floating image preview shown when hovering the Set cell. Rendered via portal so the
  // table's overflow-x: auto can't clip it.
  const [previewPos, setPreviewPos] = useState<{ left: number; top: number } | null>(null);
  const showPreview = !!wanter.imageUrl;

  return (
    <tr className="cwt-row">
      <td className="cwt-col-name">
        <Link className="cwt-name-cell" to={`/profile/u/${wanter.username}`} {...triggerProps}>
          <AvatarWithFlag
            avatarUrl={wanter.avatarUrl}
            username={wanter.username}
            size={28}
            countryCode={wanter.country}
            isElite={wanter.isElite}
            showAnimatedBorder={wanter.showAnimatedBorder}
            borderColor1={wanter.borderColor1}
            borderColor2={wanter.borderColor2}
          />
          <span className="cwt-name-text">
            {wanter.username}
            <EliteBadge isElite={!!wanter.isElite} />
          </span>
        </Link>
        {isVisible && (
          <UserHoverCard
            data={data}
            isLoading={isLoading}
            position={position}
            cardProps={cardProps}
          />
        )}
      </td>
      <td className="cwt-col-qty">{wanter.quantity}</td>
      <td className="cwt-col-set">
        <span
          className={`cwt-set-cell ${showPreview ? 'has-preview' : ''}`}
          onMouseEnter={
            showPreview ? (e) => setPreviewPos(computePreviewPos(e.currentTarget)) : undefined
          }
          onMouseLeave={showPreview ? () => setPreviewPos(null) : undefined}
        >
          <SetIcon setIconUrl={setIconUrlFor(wanter.setCode)} setName={wanter.setCode} size="sm" />
          <span>{wanter.setCode?.toUpperCase()}</span>
        </span>
        {previewPos &&
          createPortal(
            <div
              className="cwt-card-preview"
              style={{ left: previewPos.left, top: previewPos.top, width: PREVIEW_W }}
            >
              <img
                src={wanter.imageUrl}
                alt={`${cardName} — ${wanter.setName ?? wanter.setCode}`}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = AssetUrls.cards.defaultBack;
                }}
              />
            </div>,
            document.body,
          )}
      </td>
      <td className="cwt-col-foil">{getFinishSummary(wanter)}</td>
      <td className="cwt-col-condition">{formatConditions(wanter.acceptableConditions)}</td>
      <td className="cwt-col-language">{formatLanguages(wanter.acceptableLanguages)}</td>
      <td className="cwt-col-points">
        <Points points={wanter.pointValue} type="small" />
      </td>
      <td className="cwt-col-send">
        <button type="button" className="cwt-send-btn" onClick={() => onSend(wanter)}>
          Send
        </button>
      </td>
    </tr>
  );
};

const SKELETON_ROWS = 5;

export const CardWantersTable: React.FC<CardWantersTableProps> = ({
  oracleId,
  cardName,
  isLoggedIn,
  onRequireLogin,
}) => {
  const { data, isLoading } = useGetCardWantersQuery({ oracleId }, { skip: !oracleId });

  const [sortKey, setSortKey] = useState<SortKey>('points');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [compose, setCompose] = useState<ComposeState | null>(null);

  const wanters = useMemo(() => data?.wanters ?? [], [data?.wanters]);
  const totalWanters = data?.totalWanters ?? 0;

  const sorted = useMemo(() => {
    const factor = sortDir === 'asc' ? 1 : -1;
    return [...wanters].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'quantity':
          cmp = a.quantity - b.quantity;
          break;
        case 'points':
          cmp = a.pointValue - b.pointValue;
          break;
        case 'name':
          cmp = a.username.localeCompare(b.username);
          break;
        case 'set':
          cmp = (a.setCode || '').localeCompare(b.setCode || '');
          break;
        case 'foil':
          cmp = getFinishSummary(a).localeCompare(getFinishSummary(b));
          break;
        case 'condition':
          cmp = (a.condition || '').localeCompare(b.condition || '');
          break;
        case 'language':
          cmp = (a.language || '').localeCompare(b.language || '');
          break;
      }
      return cmp * factor;
    });
  }, [wanters, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // First click on a new column: numeric columns default high→low, text low→high.
      setSortDir(key === 'points' || key === 'quantity' ? 'desc' : 'asc');
    }
  };

  const handleSend = (wanter: CardWanter) => {
    if (!isLoggedIn) {
      onRequireLogin();
      return;
    }
    setCompose({
      recipient: { id: wanter.userId, username: wanter.username },
      stagedCard: wanterToStagedCard(wanter, cardName),
    });
  };

  // Zero wanters of any printing → hide the section entirely (but keep showing while loading).
  if (!isLoading && wanters.length === 0 && totalWanters === 0) {
    return null;
  }

  const showAffordabilityEmpty = !isLoading && wanters.length === 0 && totalWanters > 0;

  return (
    <div className="card-wanters-panel">
      <h2 className="cwt-title">Traders who want {cardName}</h2>

      <div className="cwt-table-scroll">
        <table className="cwt-table">
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={col.className}
                  aria-sort={
                    sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                  }
                >
                  <button
                    type="button"
                    className="cwt-th-btn"
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    <SortArrow active={sortKey === col.key} dir={sortDir} />
                  </button>
                </th>
              ))}
              <th className="cwt-col-send">Send</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              [...Array(SKELETON_ROWS)].map((_, i) => (
                <tr key={i} className="cwt-row">
                  <td className="cwt-col-name">
                    <div className="cwt-name-cell">
                      <Skeleton
                        variant="circular"
                        width={28}
                        height={28}
                        sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                      />
                      <Skeleton
                        variant="text"
                        width={110}
                        sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                      />
                    </div>
                  </td>
                  {['qty', 'set', 'foil', 'condition', 'language', 'points', 'send'].map((c) => (
                    <td key={c} className={`cwt-col-${c}`}>
                      <Skeleton
                        variant="text"
                        sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : showAffordabilityEmpty ? (
              <tr className="cwt-row">
                <td className="cwt-empty-row" colSpan={COLUMNS.length + 1}>
                  No traders who can afford this card right now. Check back later or browse other
                  cards.
                </td>
              </tr>
            ) : (
              sorted.map((wanter) => (
                <WanterRow
                  key={`${wanter.userId}-${wanter.wantListItemId}`}
                  wanter={wanter}
                  cardName={cardName}
                  onSend={handleSend}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {compose && (
        <ComposeTradeModal
          isOpen={true}
          onClose={() => setCompose(null)}
          recipient={compose.recipient}
          initialStagedCard={compose.stagedCard}
        />
      )}
    </div>
  );
};

export default CardWantersTable;
